import { useEffect, useMemo, useRef } from 'react';
import QRCodeStyling, { type Options as QrOptions } from 'qr-code-styling';
import type { QrCustomization } from '../api/types';

interface QrPreviewProps {
  data: string;
  customization: QrCustomization;
  logoUrl?: string;
  size?: number;
  className?: string;
}

function buildOptions(
  data: string,
  customization: QrCustomization,
  size: number,
  logoUrl?: string,
): QrOptions {
  return {
    width: size,
    height: size,
    type: 'svg',
    data,
    image: logoUrl,
    margin: 8,
    qrOptions: {
      errorCorrectionLevel: customization.qrOptions.errorCorrectionLevel,
    },
    dotsOptions: {
      type: customization.dotsOptions.type,
      color: customization.dotsOptions.color,
    },
    cornersSquareOptions: {
      type: customization.cornersSquareOptions.type,
      color: customization.cornersSquareOptions.color,
    },
    cornersDotOptions: {
      type: customization.cornersDotOptions.type,
      color: customization.cornersDotOptions.color,
    },
    backgroundOptions: {
      color: customization.backgroundOptions.color,
    },
    imageOptions: {
      imageSize: customization.imageOptions.imageSize,
      margin: customization.imageOptions.margin,
      hideBackgroundDots: customization.imageOptions.hideBackgroundDots,
    },
  };
}

export function QrPreview({
  data,
  customization,
  logoUrl,
  size = 240,
  className,
}: QrPreviewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<QRCodeStyling | null>(null);

  const options = useMemo(
    () => buildOptions(data, customization, size, logoUrl),
    [data, customization, size, logoUrl],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    if (!instanceRef.current) {
      instanceRef.current = new QRCodeStyling(options);
      instanceRef.current.append(containerRef.current);
    } else {
      instanceRef.current.update(options);
    }
  }, [options]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: size, height: size }}
    />
  );
}

export function downloadQr(
  data: string,
  customization: QrCustomization,
  logoUrl: string | undefined,
  filename: string,
  extension: 'png' | 'svg',
) {
  const instance = new QRCodeStyling(buildOptions(data, customization, 1024, logoUrl));
  instance.download({ name: filename, extension });
}
